<template>
  <div class="contact-review card p-4 mb-3">
    <div v-if="$slots.account">
      <h3 class="mb-1 mb-sm-4">
        {{ $t("signup.contact_review_component.signer_title") }}
      </h3>
      <slot name="account" :phoneCountryCode :accountField />
    </div>

    <div v-if="shouldShowCompanyFields">
      <h3 class="mb-1 mb-sm-2 mt-4">
        {{
          company_section_title
            ? company_section_title
            : $t("signup.contact_review_component.generator_owner_title")
        }}
      </h3>
      <p class="mb-2 mb-sm-4">
        {{
          company_section_description
            ? company_section_description
            : $t("signup.contact_review_component.generator_owner_subtext")
        }}
      </p>
    </div>

    <div v-if="isExistingCompanyWithDocuments">
      <CompanySelectDropdown
        :existingCompanies="existingCompanies"
        :selectedCompany="company"
        @selectExistingCompany="$emit('selectExistingCompany', $event)"
        @resetCompanySelection="$emit('resetCompanySelection', $event)"
      />
      <hr />
    </div>

    <div
      v-if="
        enableCompanySearch &&
        countryCode !== '' &&
        !isExistingCompanyWithDocuments
      "
    >
      <GlobalCompanySearchDropdown
        :countryCode="countryCode"
        @select="handleCompanyPicked"
      />
      <hr />
    </div>

    <slot name="company" :locked :companyField />
  </div>
</template>

<script setup lang="ts">
import GlobalCompanySearchDropdown from "@/components/common/GlobalCompanySearchDropdown.vue";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { getDialCode } from "@/utils/countryDialCodes";
import { useSignupStore } from "@/signup/signup";
import type { Account, Company, PickedCompany } from "@/types";
import { computed } from "vue";
import {
  accountFieldBinder,
  companyFieldBinder,
  type AccountRules,
  type CompanyRules,
} from "./common/contactFields";
import CompanySelectDropdown from "./CompanySelectDropdown.vue";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    companyIndex?: number;
    shouldShowCompanyFields?: boolean;
    existingCompanies?: Company[];
    enableCompanySearch?: boolean;
    countryCode?: string;
    company_section_title?: string;
    company_section_description?: string;
    existingDataOverride?: boolean;
    accountRules?: AccountRules;
    companyRules?: CompanyRules;
  }>(),
  {
    shouldShowCompanyFields: true,
    existingCompanies: () => [],
    enableCompanySearch: false,
    countryCode: "",
    company_section_title: "",
    company_section_description: "",
    existingDataOverride: false,
    accountRules: () => ({}),
    companyRules: () => ({}),
  }
);

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  // CompanySelectDropdown emits this without a payload, but the template forwards its `$event`
  // on to the parent. `undefined` keeps that forwarding type-checkable while still rejecting a
  // real value; a zero-arity signature would reject the forwarding itself.
  resetCompanySelection: [payload?: undefined];
}>();

/**
 * The step's own fields go in the slots, each a `TextRowComponent` bound with
 * `v-bind="accountField('name')"`. The binding checks the step's rules for that property and edits
 * by emitting a copy with the change applied: the account is the flow's, never mutated here.
 */
defineSlots<{
  account?: (props: {
    phoneCountryCode: string;
    accountField: ReturnType<typeof accountFieldBinder>;
  }) => unknown;
  company?: (props: {
    /** The company already has signed documents, so its saved details can't change. */
    locked: boolean;
    companyField: ReturnType<typeof companyFieldBinder>;
  }) => unknown;
}>();

const { selectedCompany } = useCompanyFinder(props.accountData);
const company = computed(() => selectedCompany.value);

// Seed the phone dial-code from the explicit prop when given (e.g. PL/ES/EE/LV/LT/SE pass a
// fixed country code), otherwise fall back to the flow's selected country, and finally the
// in-progress company's country. The default flow passes the "default" sentinel when no
// ?country= is set, so we only accept a candidate that actually resolves to a dial code and
// skip sentinels ("default", "XX", ...) that would otherwise block the fallback.
const signupStore = useSignupStore();
const phoneCountryCode = computed(
  () =>
    [props.countryCode, signupStore.country, company.value?.country].find(
      (code) => code && getDialCode(code)
    ) || ""
);

const updateAccount = (patch: Partial<Account>) => {
  emit("updateAccountData", { ...props.accountData, ...patch });
};
const updateCompany = (patch: Partial<Company>) => {
  emit("handleUpdateCompany", { ...company.value, ...patch });
};
const accountField = accountFieldBinder(
  () => props.accountData,
  updateAccount,
  () => props.accountRules
);
const companyField = companyFieldBinder(
  () => company.value,
  updateCompany,
  () => props.companyRules
);

// A registry-search result's `id` is a registry id, not an account Company.id: taking it would
// break the empty-company tracking (companyIndex resolves to -1) and collapse the contact form.
// Only its company details autofill.
const handleCompanyPicked = (picked: PickedCompany) => {
  updateCompany({ name: picked.name, country: picked.country });
};

const isExistingCompanyWithDocuments = computed(() => {
  return (
    company.value &&
    company.value.id !== undefined &&
    company.value.documents &&
    company.value.documents.length > 0
  );
});

const locked = computed(
  () =>
    Boolean(isExistingCompanyWithDocuments.value) && !props.existingDataOverride
);
</script>

<style scoped lang="scss">
.contact-review {
  .col-form-label {
    text-align: left;
    font-weight: bold;
    padding: 0;
  }
  .btn-outline-secondary {
    padding: 0.375rem 0.75rem;
  }
  .row {
    margin-left: 0;
    margin-right: 0;
  }
}

@media (max-width: 575.98px) {
  .contact-review {
    .col-form-label {
      margin-top: 1rem;
    }
  }
  h3 {
    font-size: 1.2rem;
  }
}
</style>
